import {useOverlayActivity} from "components/useOverlayActivity";
import {useMessages} from "i18n";
import {
	AfterNote, CloseButton, Content, Continue, Layout, OfferDialog, Title,
} from "upsell/ExitOfferDialog.styles";

/** The heading this dialog is named by, so the accessible name is the sentence a reader can see. */
const TITLE_ID = "referral-gift-refused-title";

/**
 * The one plain notice a referred visitor gets when the api refuses to attribute their signup
 * (kitecorp/transferify-ui#2020).
 *
 * <b>One notice for all four refusals.</b> The api tells `REFERRAL_CODE_UNKNOWN`,
 * `REFERRAL_SELF_NOT_ALLOWED`, `REFERRAL_ALREADY_ATTRIBUTED` and `REFERRAL_TOO_LATE` apart, and none of
 * those distinctions is the reader's to act on: three of them are about somebody else's account or
 * about a link they did not choose, and all four leave them in exactly the same place. Four sentences
 * would be four ways of saying the same thing, and the two the reader would recognise are the two that
 * accuse them of something.
 *
 * <b>It states that nothing was charged</b>, because this reader pressed a button labelled with a
 * price and was then signed up. Saying only that the gift failed leaves them to wonder what happened
 * to the payment.
 *
 * <b>It offers no recovery.</b> There is none: the api's verdict is final for this account, and the
 * plans are on the pricing page at their ordinary prices like they are for everyone else.
 *
 * Borrows the exit offer's own dialog primitives for the reason
 * {@link ./ReferralOfferDialog} borrows them: this is the surface that replaces that picker, and a
 * second set of styles would be one dialog drifting in two files.
 */
export function ReferralGiftRefusedNotice(
	{open, onClose, onTransitionExited}: {
		open: boolean;
		onClose: () => void;
		/** The caller owns the focus restore; see the same prop on {@link ./ReferralOfferDialog}. */
		onTransitionExited?: () => void;
	},
) {
	const {referralOffer, common} = useMessages();
	const observedExit = useOverlayActivity(open, onTransitionExited);

	return (
		<OfferDialog
			open={open}
			onClose={onClose}
			maxWidth={false}
			disableRestoreFocus={onTransitionExited !== undefined}
			slotProps={{
				paper: {"aria-labelledby": TITLE_ID},
				// The transition slot, never the Dialog's own prop: MUI runs that one while the page
				// behind is still `aria-hidden`.
				transition: {onExited: observedExit},
			}}
		>
			<Layout>
				<Content>
					<CloseButton type="button" onClick={onClose} aria-label={common.close}>✕</CloseButton>
					<Title id={TITLE_ID}>{referralOffer.refused.title}</Title>
					<AfterNote>{referralOffer.refused.body}</AfterNote>
					<Continue type="button" onClick={onClose} data-testid="referral-gift-refused-close">
						{referralOffer.refused.cta}
					</Continue>
				</Content>
			</Layout>
		</OfferDialog>
	);
}
